import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import {
  Modal,
  Button,
  HeadingText,
  ParagraphText,
  StackLayout,
  ImageMessage,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import leaveWithoutSavingIllustration from 'assets/leave-without-saving-illustration.svg';

type LeaveWithoutSavingModalProps = {
  // Block navigation (and show the modal) while the page has unsaved changes.
  when: boolean;
};

/**
 * Confirmation modal shown when a member tries to leave a page that has unsaved
 * changes (GEIS-358 — Figma "4.5.3 Leave Without Saving").
 *
 * In-app navigation is intercepted with react-router's `useBlocker` (the app
 * uses a data router — see `app.tsx` — so the hook is supported). Full-page
 * exits that `useBlocker` can't see (reload, tab close, navigating to an
 * external URL) fall back to the native `beforeunload` prompt.
 *
 * Genesis `Modal`'s default `standard` layout already renders a centred dialog
 * on desktop and a bottom sheet on mobile, matching both Figma frames.
 */
export const LeaveWithoutSavingModal = ({
  when,
}: LeaveWithoutSavingModalProps) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));
  const blocker = useBlocker(when);
  const isBlocked = blocker.state === 'blocked';

  // Native browser prompt for reload / tab close / navigation outside the SPA,
  // which `useBlocker` does not intercept.
  useEffect(() => {
    if (!when) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // preventDefault is the modern trigger for the native "leave site?"
      // prompt; legacy Chrome/Safari also require returnValue to be set.
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  // If the page stops being dirty while a block is pending (e.g. a successful
  // save), release the blocker so the queued navigation isn't left hanging.
  useEffect(() => {
    if (blocker.state === 'blocked' && !when) {
      blocker.reset();
    }
  }, [blocker, when]);

  const stayOnPage = () => {
    if (blocker.state === 'blocked') blocker.reset();
  };
  const leavePage = () => {
    if (blocker.state === 'blocked') blocker.proceed();
  };

  return (
    <Modal.Root
      open={isBlocked}
      onOpenChange={(open) => {
        // Closing via Escape, overlay click, or the "X" is treated the same as
        // "Continue making changes": keep the member on the page.
        if (!open) stayOnPage();
      }}
    >
      <Modal.Content
        width={{ '@initial': 640, '@mobile': '100%' }}
        // Control padding ourselves so top/bottom can differ per breakpoint.
        padding="$none"
        // Hide the "X" on mobile (the bottom sheet is dismissed via the
        // actions); desktop keeps Genesis's close-button header row.
        showCloseButton={!isBelowTablet}
      >
        <StackLayout
          horizontalAlignment="stretch"
          spacing="$none"
          css={{
            paddingInline: '$oneAndHalf',
            // Desktop: the close-button row already supplies the top inset, so
            // sit flush. Mobile: no close button, so add a top inset.
            paddingBlockStart: isBelowTablet ? '$oneAndHalf' : '$none',
            paddingBlockEnd: isBelowTablet ? '$one' : '$oneAndHalf',
          }}
        >
          <ImageMessage
            image={leaveWithoutSavingIllustration}
            imageAlt=""
            imagePosition="top"
            imageWidth={160}
            imageGap="$oneAndHalf"
          >
            <StackLayout
              spacing="$half"
              horizontalAlignment={isBelowTablet ? 'center' : 'start'}
              css={{ textAlign: isBelowTablet ? 'center' : 'left' }}
            >
              <Modal.Title>
                <HeadingText level="2" size="lg">
                  {formatMessage({ id: 'STR_LEAVE_WITHOUT_SAVING_TITLE' })}
                </HeadingText>
              </Modal.Title>
              <Modal.Description>
                <ParagraphText css={{ color: '$onSurfaceTextSubdued' }}>
                  {formatMessage({ id: 'STR_LEAVE_WITHOUT_SAVING_BODY' })}
                </ParagraphText>
              </Modal.Description>
            </StackLayout>
          </ImageMessage>
          <StackLayout
            orientation={isBelowTablet ? 'vertical' : 'horizontal'}
            spacing="$one"
            horizontalAlignment={isBelowTablet ? 'stretch' : 'start'}
            css={{ marginBlockStart: '$oneAndHalf' }}
          >
            <Button
              priority="primary"
              onClick={stayOnPage}
              width={isBelowTablet ? 'fillContainer' : 'hugContents'}
            >
              {formatMessage({ id: 'STR_LEAVE_WITHOUT_SAVING_STAY' })}
            </Button>
            <Button
              priority="secondary"
              onClick={leavePage}
              width={isBelowTablet ? 'fillContainer' : 'hugContents'}
            >
              {formatMessage({ id: 'STR_LEAVE_WITHOUT_SAVING_LEAVE' })}
            </Button>
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
